import React, { useMemo, useState, useCallback } from 'react';
import { View, StyleSheet, Text, TouchableOpacity } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { ADMIN_COLORS } from '@/constants/admin';
import { ICommunityResource } from '@/types/admin/resources';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ResourceIcon } from '@/components/admin/resources/common/ResourceIcon';
import { getResourceItemCount } from '@/components/admin/resources/common/resourceUtils';
import { DesktopCreateResourcePanel } from '@/components/admin/resources/desktop/DesktopCreateResourcePanel';
import { DesktopEditResourcePanel } from '@/components/admin/resources/desktop/DesktopEditResourcePanel';
import { SelectionIndicator } from '@/components/common/admin/mobile/SelectionIndicator';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';

export type DesktopResourceTile = {
  position: number;
  resource?: ICommunityResource;
};

interface DesktopResourcesViewProps {
  tiles: DesktopResourceTile[];
  isLoading?: boolean;
  highestPosition?: number;
  onCreateResource: () => void;
  onEditResource: (resource: ICommunityResource) => void;
  onAssignEmptySlot: (resourceId: number, position: number) => Promise<void> | void;
  onUpdateResourcePositions: (resourceId: number, position: number) => Promise<void> | void;
  selectedResourceId: number | null;
  onSelectResource: (resourceId: number | null) => void;
  onRefresh?: () => void;
}

export const DesktopResourcesView: React.FC<DesktopResourcesViewProps> = ({
  tiles,
  isLoading = false,
  highestPosition,
  onCreateResource,
  onEditResource,
  onAssignEmptySlot,
  onUpdateResourcePositions,
  selectedResourceId,
  onSelectResource,
  onRefresh,
}) => {
  const [showCreate, setShowCreate] = useState(false);
  const [editResource, setEditResource] = useState<ICommunityResource | null>(null);
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();

  const primaryTiles = useMemo(() => tiles.filter(t => t.position < 4), [tiles]);
  const secondaryTiles = useMemo(() => tiles.filter(t => t.position >= 4), [tiles]);

  const renderTile = (tile: DesktopResourceTile) => {
    if (tile.resource) {
      const totalItems = getResourceItemCount(tile.resource);

      const isSelected = selectedResourceId === tile.resource.id;
      return (
        <TouchableOpacity
          key={`res-${tile.resource.id}`}
          style={[styles.tile, isSelected && styles.tileSelected]}
          activeOpacity={0.8}
          onPress={async () => {
            // If a different resource is selected, move it onto this occupied tile (parity with admin-web drag onto bubble)
            if (selectedResourceId != null && selectedResourceId !== tile.resource!.id) {
              try {
                await onUpdateResourcePositions(selectedResourceId, tile.resource!.position);
                showSuccess('Resource moved');
              } catch (err: any) {
                showApiError(err, 'Failed to move resource');
              } finally {
                onSelectResource(null);
              }
              return;
            }
            onSelectResource(isSelected ? null : tile.resource!.id);
          }}
        >
          <View style={styles.tileHeader}>
            <View style={styles.iconCircle}>
              <ResourceIcon icon={tile.resource.icon} size={16} color={ADMIN_COLORS.TEXT.PRIMARY} />
            </View>
            <View style={styles.selectIndicator}>
              <SelectionIndicator selected={isSelected} size={16} />
            </View>
          </View>

          <View style={styles.titleRow}>
            <Text style={styles.title} numberOfLines={1}>{tile.resource.name}</Text>
            {totalItems > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{totalItems}</Text>
              </View>
            )}
          </View>

          <Text style={styles.description} numberOfLines={3}>{tile.resource.description}</Text>

          <TouchableOpacity style={styles.viewEditBtn} onPress={() => {
            setEditResource(tile.resource!);
          }}>
            <Text style={styles.viewEditText}>View/Edit</Text>
          </TouchableOpacity>
        </TouchableOpacity>
      );
    }

    // Empty slot
    return (
      <TouchableOpacity
        key={`empty-${tile.position}`}
        style={[styles.tile, styles.emptyTile]}
        onPress={async () => {
          if (selectedResourceId != null) {
            try {
              await onAssignEmptySlot(selectedResourceId, tile.position);
              showSuccess('Resource moved');
            } catch (err: any) {
              showApiError(err, 'Failed to move resource');
            } finally {
              onSelectResource(null);
            }
            return;
          }
          setShowCreate(true);
        }}
        activeOpacity={0.8}
      >
        <View style={styles.emptyContent}>
          <SVG_COMPONENTS.ADD_BUTTON width={20} height={20} color={ADMIN_COLORS.TEXT_TERTIARY} />
          <Text style={styles.emptyText}>{selectedResourceId != null ? 'Move here' : 'Add resource'}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const renderPrimaryGrid = () => (
    <View style={styles.grid4Cols}>
      {Array.from({ length: 4 }).map((_, i) => {
        const tile = primaryTiles.find(t => t.position === i) || { position: i } as DesktopResourceTile;
        return (
          <View key={`p-${i}`} style={styles.gridCell}>
            {renderTile(tile)}
          </View>
        );
      })}
    </View>
  );

  const renderSecondaryGrid = () => {
    if (secondaryTiles.length === 0) return null;
    const rows: DesktopResourceTile[][] = [];
    for (let i = 0; i < secondaryTiles.length; i += 4) {
      rows.push(secondaryTiles.slice(i, i + 4));
    }
    return (
      <View style={{ gap: 8 }}>
        {rows.map((row, idx) => (
          <View key={`row-${idx}`} style={styles.grid4Cols}>
            {row.map((tile, j) => (
              <View key={`s-${idx}-${j}`} style={styles.gridCell}>
                {renderTile(tile)}
              </View>
            ))}
          </View>
        ))}
      </View>
    );
  };

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader 
        title="Resources" 
        subtitle="Manage community resources and their display order on the residents' app"
        showAddButton
        addButtonText="New resource"
        onAddPress={() => setShowCreate(true)}
      />

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Residents dashboard</Text>
        {renderPrimaryGrid()}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Other resources</Text>
        {renderSecondaryGrid()}
      </View>
      {showCreate && (
        <DesktopCreateResourcePanel 
          visible={true}
          onClose={() => setShowCreate(false)}
          highestPosition={highestPosition ?? 0}
          onSuccess={() => {
            onRefresh?.();
          }}
        />
      )}
      {editResource && (
        <DesktopEditResourcePanel visible={true} onClose={() => setEditResource(null)} resource={editResource} />
      )}
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 12,
  },
  grid4Cols: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    // Use horizontal padding on cells instead of gap to keep 4 per row
    marginHorizontal: -6,
  },
  gridCell: {
    flexBasis: '25%',
    maxWidth: '25%',
    paddingHorizontal: 6,
    marginBottom: 12,
  },
  tile: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    padding: 12,
    height: 200,
  },
  tileSelected: {
    borderColor: ADMIN_COLORS.PRIMARY,
    borderWidth: 2,
  },
  emptyTile: {
    borderStyle: 'dashed',
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tileHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  iconCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dragHandle: {
    padding: 4,
  },
  selectIndicator: {
    padding: 4,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    flex: 1,
  },
  badge: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  badgeText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  description: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    lineHeight: 16,
    marginTop: 8,
    flex: 1,
  },
  viewEditBtn: {
    marginTop: 8,
  },
  viewEditText: {
    fontSize: 12,
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '600',
  },
  emptyContent: {
    alignItems: 'center',
    gap: 8,
  },
  emptyText: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_TERTIARY,
    fontWeight: '500',
  },
});


